import { useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, InputField, Button } from '@ets/design-system';

import { toast } from 'utils/toast';
import { useDispatch } from '_common/hooks';
import { useSuiteObject } from '_common/suite';
import { useModalData } from 'App/ModalContext/ModalContext';
import { closeModal } from 'App/ModalContext/utils';
import objectApi from 'App/redux/objectApi';
import { Textarea, type TextareaAPI, type TextareaProps } from '_common/lib/components';

export type SaveVersionModalProps = {
  saveVersion: (description: string) => void;
  editDescription?: (description: string, index: number) => void;
};

const SaveVersionModal = ({ saveVersion, editDescription }: SaveVersionModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const object = useSuiteObject();
  const modalData = useModalData('SaveVersionModal');

  const [description, setDescription] = useState(modalData?.initialDescription ?? '');
  const [validations, setValidations] = useState<{ description?: string }>({});
  const textareaRef = useRef<TextareaAPI>(null);

  useLayoutEffect(() => {
    if (modalData) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    }
  }, []);

  if (!modalData) {
    return null;
  }

  const { type, index } = modalData;

  const close = () => {
    closeModal('SaveVersionModal');
  };

  const handleOnChange: TextareaProps['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    const descriptionValue = target.value;
    setDescription(descriptionValue);

    const newValidations: typeof validations = {};
    if (descriptionValue.length > 256) {
      newValidations.description = intl.formatMessage(
        {
          id: 'ERROR_VERSION_DESCRIPTION',
        },
        { length: 256 },
      );
    }

    setValidations(newValidations);
  };

  const handleSaveVersion = async () => {
    try {
      switch (type) {
        case 'saveVersion': {
          await saveVersion(description);
          toast({
            variant: 'success',
            title: 'VERSION_SAVED',
            message: 'THE_VERSION_WAS_SUCCESSFULLY_SAVED',
          });
          break;
        }
        case 'editDescription': {
          if (editDescription) {
            await editDescription(description, index);
            toast({
              variant: 'success',
              title: 'VERSION_DESCRIPTION_EDITED',
              message: 'THE_VERSION_DESCRIPTION_WAS_SUCCESSFULLY_EDITED',
            });
          }
          break;
        }
        default:
          break;
      }

      dispatch(objectApi.util.invalidateTags([{ type: object.type, id: object.id }]));
    } catch (e) {
      throw e;
    }

    close();
  };

  return (
    <Modal width="67rem" open close={close} testId="save-version">
      <Modal.Header>
        <FormattedMessage id={type === 'saveVersion' ? 'SAVE_VERSION' : 'EDIT_DESCRIPTION'} />
      </Modal.Header>
      <Modal.Body>
        <InputField
          size="l"
          label={intl.formatMessage({ id: 'VERSION_DESCRIPTION' })}
          feedbackContent={validations.description}
          testId="save-version-modal-description"
        >
          <Textarea
            plainText
            name="version-description"
            type="text"
            size="m"
            placeholder={
              type === 'saveVersion'
                ? intl.formatMessage({ id: 'INSERT_YOUR_DESCRIPTION' })
                : intl.formatMessage({ id: 'EDIT_VERSION_DESCRIPTION_PLACEHOLDER' })
            }
            onChange={handleOnChange}
            initialContent={description}
            hasError={!!validations.description}
            ref={textareaRef}
            testId="save-version-modal-description-textarea"
          />
        </InputField>
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="save-version-modal-cancel">
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          variant="primary"
          style={{ margin: '0 0 0 1rem' }}
          onClick={handleSaveVersion}
          disabled={!!validations.description}
          testId="save-version-modal-save"
        >
          <FormattedMessage id="SAVE_VERSION" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveVersionModal;
